import { ArrowUpRight, Check, Zap, Mail, BarChart3, Users, TrendingUp, Shield, Clock, Globe, Building2, ShoppingBag, GraduationCap, Heart, Plane, Calendar, CreditCard, Box, MessageSquare, Target, FileText, Layers, Send, Percent, Archive, CheckCircle, AlertCircle, Info, Megaphone, Filter, PieChart, Sparkles, MailCheck, Inbox, ChevronDown, ChevronUp } from 'lucide-react';
import SEO from '@/components/SEO';
import BrandMarquee from '@/components/BrandMarquee';
import { site } from '@/data/content';
import { useState } from 'react';

const features = [
  { icon: MailCheck, title: 'Drag-and-Drop Builder', text: 'Create stunning emails with our intuitive drag-and-drop builder. No coding required. Choose from professional templates or build from scratch.' },
  { icon: Filter, title: 'Advanced Segmentation', text: 'Segment your audience based on behavior, demographics, purchase history, and engagement. Send targeted campaigns that convert.' },
  { icon: Zap, title: 'Automated Workflows', text: 'Set up automated email sequences for welcome emails, abandoned cart recovery, onboarding, and re-engagement campaigns.' },
  { icon: PieChart, title: 'A/B Testing', text: 'Test subject lines, content, send times, and CTAs. Optimize your campaigns with data-driven insights and improve open rates.' },
  { icon: BarChart3, title: 'Real-time Analytics', text: 'Track opens, clicks, bounces, unsubscribes, and conversions in real-time. Measure ROI and optimize performance.' },
  { icon: Inbox, title: 'Smart Deliverability', text: '95%+ delivery rate with SPF, DKIM, and DMARC authentication. Maintain sender reputation and inbox placement.' },
];

const benefits = [
  { title: '95% email delivery rate', text: 'Our email infrastructure ensures 95%+ delivery rates with proper authentication, IP warming, and deliverability monitoring.' },
  { title: 'Advanced automation workflows', text: 'Create complex automated sequences with triggers, delays, and conditions. Nurture leads and convert customers automatically.' },
  { title: 'Professional email templates', text: 'Choose from 50+ responsive email templates across industries. Customize with your brand colors, fonts, and imagery.' },
  { title: 'Segmentation & personalization', text: 'Segment your audience based on behavior, demographics, and engagement. Personalize content with dynamic fields and merge tags.' },
];

const useCases = [
  { title: 'Welcome email sequences', text: 'Automatically send welcome emails to new subscribers. Introduce your brand, set expectations, and drive engagement from day one.' },
  { title: 'Abandoned cart recovery', text: 'Recover lost sales with automated abandoned cart emails. Send timely reminders with product images and personalized recommendations.' },
  { title: 'Newsletter campaigns', text: 'Send regular newsletters with curated content, product updates, and company news. Keep your audience engaged and informed.' },
  { title: 'Promotional campaigns', text: 'Launch promotional campaigns for sales, new products, and special offers. Drive traffic and conversions with targeted email marketing.' },
];

const industries = [
  { title: 'E-commerce & Retail', text: 'Recover 30% of abandoned carts with automated email sequences. Send product recommendations and seasonal promotions.' },
  { title: 'SaaS & Technology', text: 'Nurture leads with automated onboarding sequences. Send product updates, feature announcements, and renewal reminders.' },
  { title: 'Financial Services', text: 'Send secure transaction alerts, account statements, and compliance notifications. Maintain trust with professional email communications.' },
  { title: 'Education & Training', text: 'Increase course completion rates with automated email reminders. Share learning resources and certificate notifications.' },
];

const pricing = [
  { tier: 'Starter', price: 'Free', unit: 'forever', features: ['Up to 2,000 subscribers', '10,000 emails/month', 'Basic templates', 'Email scheduling', 'Standard support'] },
  { tier: 'Professional', price: '$49', unit: '/month', features: ['Up to 25,000 subscribers', 'Unlimited emails', 'Advanced automation', 'A/B testing', 'Priority support'], highlight: true },
  { tier: 'Enterprise', price: 'Custom', unit: 'pricing', features: ['Unlimited subscribers', 'Dedicated IP', 'Custom integrations', 'Account manager', '24/7 support'] },
];

const faqs: Array<{ question: string; answer: string }> = [
  {
    question: 'What is email marketing automation?',
    answer: 'Email marketing automation is sending targeted emails based on triggers, schedules, or user behavior. It includes welcome sequences, abandoned cart recovery, drip campaigns, and re-engagement workflows that run automatically once set up.',
  },
  {
    question: 'How does email segmentation work?',
    answer: 'Email segmentation divides your audience into groups based on criteria like demographics, behavior, purchase history, engagement level, and preferences. This allows you to send highly relevant, personalized campaigns that perform better.',
  },
  {
    question: 'What is A/B testing in email marketing?',
    answer: 'A/B testing sends different versions of your email to small segments of your audience to test variables like subject lines, content, send times, or CTAs. The winning version is then sent to the remaining audience to optimize performance.',
  },
  {
    question: 'How do I improve email deliverability?',
    answer: 'Improve deliverability by authenticating your domain with SPF, DKIM, and DMARC, maintaining sender reputation, cleaning your email list regularly, using double opt-in, and following email marketing best practices.',
  },
  {
    question: 'Can I integrate email marketing with other channels?',
    answer: 'Yes. Email marketing integrates with your CRM, e-commerce platform, and other marketing tools. You can trigger emails based on website behavior, purchase history, and customer interactions across channels.',
  },
  {
    question: 'What is the difference between drip campaigns and automated workflows?',
    answer: 'Drip campaigns are pre-scheduled sequences sent at fixed intervals. Automated workflows are more complex, with triggers, conditions, and branching logic based on user behavior and actions.',
  },
  {
    question: 'How do I measure email marketing ROI?',
    answer: 'Track metrics like open rate, click-through rate, conversion rate, revenue per email, and customer lifetime value. Use analytics to attribute revenue to specific campaigns and calculate ROI.',
  },
  {
    question: 'What email templates are available?',
    answer: 'Choose from 50+ professional, responsive email templates across industries including e-commerce, SaaS, education, finance, and more. All templates are customizable with your brand colors and fonts.',
  },
];

export default function EmailMarketing() {
  const [openFaq, setOpenFaq] = useState<string | null>(null);

  return (
    <>
      <SEO
        title="Email Marketing Automation Platform | Professional Email Campaigns"
        description="Professional email marketing with drag-and-drop builder, advanced segmentation, automated workflows, A/B testing, and 95% delivery rates. Perfect for e-commerce, SaaS, and B2B."
        path="/email-marketing"
        keywords="email marketing, email automation, email campaigns, email segmentation, email templates, drip campaigns, email sequences, email newsletters, email builder, email marketing software"
      />
      <section className="relative overflow-hidden bg-slate-950 px-4 pt-32 pb-32 text-white sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-indigo-900/40 via-slate-950 to-slate-950" />
        <div className="relative mx-auto max-w-6xl">
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-indigo-500/30 bg-indigo-500/10 px-4 py-1.5 text-sm font-semibold text-indigo-300 backdrop-blur">
                <CheckCircle className="h-4 w-4" /> 95% delivery rate • 50+ templates
              </span>
              <h1 className="mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
                Professional <span className="gradient-text">email marketing</span> automation
              </h1>
              <p className="mt-6 text-lg text-slate-300">
                Create stunning email campaigns with drag-and-drop builder, advanced segmentation, automated workflows, and A/B testing. Perfect for e-commerce, SaaS, and B2B marketing.
              </p>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <a
                  href={site.workspaceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-8 py-4 text-base font-bold text-slate-900 transition hover:bg-slate-100 shadow-xl shadow-white/10"
                >
                  Start Free Trial <ArrowUpRight className="h-4 w-4" />
                </a>
                <a
                  href="#pricing"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-600 bg-slate-900/50 px-8 py-4 text-base font-semibold text-white backdrop-blur transition hover:border-indigo-500 hover:bg-slate-800"
                >
                  View Pricing <BarChart3 className="h-4 w-4" />
                </a>
              </div>
            </div>
            <div className="relative hidden lg:block">
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-600/20 to-indigo-400/10 blur-3xl" />
              <div className="relative overflow-hidden rounded-3xl border border-slate-800 bg-slate-900/60 p-2 backdrop-blur">
                <img
                  src="https://media.getzurihub.com/Jayrah%20Assets/Email_Delivery.webp"
                  alt="Email marketing platform showing drag-and-drop builder and automation workflows"
                  className="rounded-2xl w-full h-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <BrandMarquee compact />

      <section className="py-24" aria-labelledby="features-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="features-heading" className="section-title">Enterprise email marketing features</h2>
            <p className="section-subtitle mx-auto mt-4">Professional features designed to maximize your email campaign performance and ROI.</p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {features.map((f) => (
              <div key={f.title} className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm transition hover:-translate-y-1 hover:shadow-lg">
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600">
                  <f.icon className="h-7 w-7" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">{f.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="benefits-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="benefits-heading" className="section-title">Why businesses choose email marketing</h2>
            <p className="section-subtitle mx-auto mt-4">Advantages that set our email marketing platform apart from competitors.</p>
          </div>
          <div className="grid gap-8 md:grid-cols-2">
            {benefits.map((b) => (
              <div key={b.title} className="flex gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-indigo-100 text-indigo-600">
                  <Check className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900">{b.title}</h3>
                  <p className="mt-2 text-sm text-slate-600">{b.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" aria-labelledby="use-cases-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="use-cases-heading" className="section-title">Email marketing use cases for your business</h2>
            <p className="section-subtitle mx-auto mt-4">Practical applications of email marketing across different business scenarios.</p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {useCases.map((u) => (
              <div key={u.title} className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
                  <Megaphone className="h-6 w-6" />
                </div>
                <h3 className="text-base font-bold text-slate-900">{u.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{u.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="industries-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="industries-heading" className="section-title">Industries powered by email marketing</h2>
            <p className="section-subtitle mx-auto mt-4">From e-commerce to SaaS, businesses across all industries use email marketing.</p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {industries.map((i) => (
              <div key={i.title} className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
                  <Building2 className="h-6 w-6" />
                </div>
                <h3 className="text-base font-bold text-slate-900">{i.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{i.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="pricing" className="py-24" aria-labelledby="pricing-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="pricing-heading" className="section-title">Simple pricing with no hidden fees</h2>
            <p className="section-subtitle mx-auto mt-4">Start free and scale as you grow. No contracts, no hidden fees, cancel anytime.</p>
          </div>
          <div className="grid gap-8 md:grid-cols-3">
            {pricing.map((p) => (
              <div key={p.tier} className={`rounded-3xl border ${p.highlight ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 bg-white'} p-8 shadow-sm`}>
                <h3 className="text-lg font-bold text-slate-900">{p.tier}</h3>
                <p className="mt-2 text-3xl font-extrabold text-slate-900">{p.price} <span className="text-lg font-normal text-slate-600">{p.unit}</span></p>
                <ul className="mt-6 space-y-3">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm text-slate-600">
                      <CheckCircle className="h-5 w-5 shrink-0 text-indigo-600" />
                      {f}
                    </li>
                  ))}
                </ul>
                <a
                  href={site.workspaceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`mt-8 block w-full rounded-full px-6 py-3 text-center text-base font-semibold transition ${p.highlight ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'bg-slate-900 text-white hover:bg-slate-800'}`}
                >
                  Get Started
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" aria-labelledby="faq-heading">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="faq-heading" className="section-title">Frequently asked questions</h2>
            <p className="section-subtitle mx-auto mt-4">Everything you need to know about email marketing automation.</p>
          </div>
          <div className="space-y-4">
            {faqs.map((faq) => (
              <div key={faq.question} className="rounded-2xl border border-slate-200 bg-white">
                <button
                  onClick={() => setOpenFaq(openFaq === faq.question ? null : faq.question)}
                  className="flex w-full items-center justify-between p-6 text-left"
                >
                  <span className="text-base font-semibold text-slate-900">{faq.question}</span>
                  {openFaq === faq.question ? (
                    <ChevronUp className="h-5 w-5 text-slate-500" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-slate-500" />
                  )}
                </button>
                {openFaq === faq.question && (
                  <div className="px-6 pb-6">
                    <p className="text-sm text-slate-600">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-950 py-24 text-white">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <h2 className="text-3xl font-extrabold tracking-tight sm:text-4xl">
            Ready to automate your email marketing?
          </h2>
          <p className="mt-4 text-lg text-slate-300">
            Start your free trial today with 50+ professional templates and advanced automation workflows.
          </p>
          <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:justify-center">
            <a
              href={site.workspaceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-8 py-4 text-base font-bold text-slate-900 transition hover:bg-slate-100"
            >
              Get Started Free <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
